import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Bot, Play, Pause, RefreshCw, Save, Zap } from "lucide-react";

/**
 * KidBotsAdminTab — manage the 25 kid bot personas.
 *
 * What admins can do here:
 *  · Seed / re-seed the 25 bots (idempotent)
 *  · Enable / disable a bot (stops it from auto-posting)
 *  · Edit a bot's bio + emoji avatar
 *  · Fire a manual post right now (useful for demos + content seeding)
 *  · Global kill-switch pause / resume all bot activity
 */
export default function KidBotsAdminTab() {
  const [bots, setBots] = useState([]);
  const [cfg, setCfg] = useState({ paused: false, post_cadence_min: 30 });
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editBio, setEditBio] = useState("");
  const [editEmoji, setEditEmoji] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [b, c] = await Promise.all([
        api.get("/admin/kids/bots"),
        api.get("/admin/kids/bots/config"),
      ]);
      setBots(b.data?.bots || []);
      setCfg(c.data || {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const seed = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/kids/bots/seed");
      toast.success(`Seed complete — ${r.data.created} new bots added (${r.data.total_seeds} total)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const toggle = async (bot) => {
    try {
      await api.patch(`/admin/kids/bots/${bot.user_id}`, { bot_enabled: !bot.bot_enabled });
      setBots((b) => b.map((x) => x.user_id === bot.user_id ? { ...x, bot_enabled: !bot.bot_enabled } : x));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const postNow = async (bot) => {
    try {
      await api.post(`/admin/kids/bots/${bot.user_id}/post-now`);
      toast.success(`${bot.name} posted! 🎉`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveEdit = async (bot) => {
    try {
      await api.patch(`/admin/kids/bots/${bot.user_id}`, { bio: editBio, avatar_emoji: editEmoji });
      toast.success("Updated");
      setEditingId(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const togglePause = async () => {
    try {
      const ep = cfg.paused ? "resume-all" : "pause-all";
      await api.post(`/admin/kids/bots/${ep}`);
      toast.success(cfg.paused ? "Bots resumed" : "All bots paused");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section data-testid="kid-bots-admin-tab" className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-black text-2xl text-white flex items-center gap-2">
            <Bot className="w-6 h-6 text-pink-400" /> Kid Bots
          </h2>
          <p className="text-white/60 text-sm mt-1">
            25 cheerful kid personas that auto-post to the Kids feed. Keeps the network lively between real creators.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="kidbots-seed-btn"
            onClick={seed}
            disabled={busy}
            className="bg-white/10 hover:bg-white/20 text-white font-bold px-3 py-2 rounded-lg text-sm inline-flex items-center gap-2 disabled:opacity-50"
          ><RefreshCw className="w-4 h-4" /> Seed 25 bots</button>
          <button
            data-testid="kidbots-pause-btn"
            onClick={togglePause}
            className={`font-bold px-3 py-2 rounded-lg text-sm inline-flex items-center gap-2 ${cfg.paused ? "bg-emerald-500 hover:bg-emerald-400 text-black" : "bg-red-500/80 hover:bg-red-500 text-white"}`}
          >
            {cfg.paused
              ? <><Play className="w-4 h-4" /> Resume all</>
              : <><Pause className="w-4 h-4" /> Pause all</>}
          </button>
        </div>
      </div>

      <div data-testid="kidbots-status-chip" className={`inline-flex items-center gap-2 text-xs font-bold px-3 py-1.5 rounded-full ${cfg.paused ? "bg-red-500/20 text-red-300" : "bg-emerald-500/20 text-emerald-300"}`}>
        <span className={`w-2 h-2 rounded-full ${cfg.paused ? "bg-red-400" : "bg-emerald-400 animate-pulse"}`} />
        {cfg.paused ? "Bots paused" : "Bots active"} · cadence {cfg.post_cadence_min}min · quiet hours {(cfg.quiet_hours_utc || []).join(",")}
      </div>

      {loading && <div className="text-white/50 text-sm">Loading bots…</div>}

      {!loading && (
        <div data-testid="kidbots-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {bots.map((b) => (
            <div key={b.user_id} data-testid={`kidbot-row-${b.user_id}`} className="bg-black/40 border border-white/10 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="text-3xl select-none leading-none">{b.avatar_emoji || "⭐"}</div>
                <div className="min-w-0 flex-1">
                  <div className="text-white font-bold truncate">{b.name}</div>
                  <div className="text-white/50 text-xs truncate">@{b.handle} · age {b.age}</div>
                  <div className="text-white/70 text-xs mt-1 line-clamp-2">{b.bio}</div>
                </div>
                <label className="inline-flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid={`kidbot-enabled-${b.user_id}`}
                    checked={!!b.bot_enabled}
                    onChange={() => toggle(b)}
                    className="sr-only peer"
                  />
                  <span className="w-8 h-5 bg-white/15 rounded-full relative peer-checked:bg-emerald-500 transition">
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition ${b.bot_enabled ? "translate-x-3" : ""}`} />
                  </span>
                </label>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {(b.interests || []).map((i) => (
                  <span key={i} className="text-[10px] bg-white/10 text-white/70 px-2 py-0.5 rounded-full">{i}</span>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-3">
                <button
                  data-testid={`kidbot-post-${b.user_id}`}
                  onClick={() => postNow(b)}
                  className="text-xs font-bold bg-pink-500 hover:bg-pink-400 text-white px-3 py-1.5 rounded-full inline-flex items-center gap-1"
                ><Zap className="w-3 h-3" /> Post now</button>
                <button
                  data-testid={`kidbot-edit-${b.user_id}`}
                  onClick={() => { setEditingId(b.user_id); setEditBio(b.bio || ""); setEditEmoji(b.avatar_emoji || ""); }}
                  className="text-xs font-bold bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-full"
                >Edit</button>
                {b.last_post_at && <span className="ml-auto text-[10px] text-white/40">last: {new Date(b.last_post_at).toLocaleTimeString()}</span>}
              </div>
              {editingId === b.user_id && (
                <div data-testid={`kidbot-edit-panel-${b.user_id}`} className="mt-3 space-y-2 bg-black/30 border border-white/10 rounded-lg p-3">
                  <input
                    data-testid={`kidbot-edit-emoji-${b.user_id}`}
                    value={editEmoji}
                    onChange={(e) => setEditEmoji(e.target.value.slice(0, 4))}
                    placeholder="emoji"
                    className="w-16 bg-white/10 border border-white/20 rounded-md px-2 py-1 text-white text-xl text-center"
                  />
                  <textarea
                    data-testid={`kidbot-edit-bio-${b.user_id}`}
                    value={editBio}
                    onChange={(e) => setEditBio(e.target.value.slice(0, 200))}
                    rows={2}
                    className="w-full bg-white/10 border border-white/20 rounded-md px-2 py-1 text-white text-xs"
                  />
                  <div className="flex gap-2">
                    <button data-testid={`kidbot-save-${b.user_id}`} onClick={() => saveEdit(b)} className="text-xs font-bold bg-emerald-500 text-black px-3 py-1 rounded-full inline-flex items-center gap-1"><Save className="w-3 h-3" /> Save</button>
                    <button onClick={() => setEditingId(null)} className="text-xs font-bold bg-white/10 text-white px-3 py-1 rounded-full">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
